import { IEidosTooltip } from "@app/core/directives/eidos-tooltip.directive";
import DevExpress from "devextreme";
import { CellPreparedEvent, EditingStartEvent, RowClickEvent } from 'devextreme/ui/data_grid';

import { BehaviorSubject } from "rxjs";
import { v4 as uuid } from 'uuid';

export type EidosMatrixValue = number | string | Date | boolean | object;
export type EidosMatrixData = Array<Array<IEidosMatrixValue>>;
export interface IEidosMatrixValue {
  [key: string]: EidosMatrixValue;
}
export interface IEidosMatrixValueFun {
  [key: string]: (newValue: EidosMatrixValue, data: EidosMatrixData, matrix: IEidosMatrixApi) => void;
}
export interface IEidosMatrixGenericFun {
  [key: string]: (...args: any[]) => any;
}
export interface IMatrixCellStatus {
  isTouched: boolean;
  isManualTouched: boolean;
}
export interface IEidosMatrixApi {
  setValue(rif: IMatrixRif, value: EidosMatrixValue): EidosMatrixValue;
  onCellChanges(e: Array<DevExpress.ui.dxDataGrid.DataChange>, sheet: EidosMatrixSheet, matrix: IEidosMatrixApi): void;
  getRowIndex(sheet: EidosMatrixSheet, key: any): number | null;
  execAction(action: string, sheet: EidosMatrixSheet): void;
  addRow(sheet: EidosMatrixSheet, newRow: IEidosMatrixValue, position?: number): void;
  addColumn(sheet: EidosMatrixSheet, newColumn: IEidosMatrixColumn, position?: number): void;
  getMatrixCellValue(rif: IMatrixRif): EidosMatrixValue;
  expandSheetAllRif(exp: string, rangeType: MatrixFormulaRangeType, start: number, end: number): string;
  expandRowAllRif(exp: string, rangeType: MatrixFormulaRangeType, start: number, end: number): string;
  expandColAllRif(exp: string, rangeType: MatrixFormulaRangeType, start: number, end: number): string;
  expandRangeRif(exp: string, current: IMatrixRif, rangeType: MatrixFormulaRangeType): string;
  evaluateRif(rif: string, current: IMatrixRif): IMatrixRif;
  resolveFormula(formula: string, cur: IMatrixRif, skipCondition:string): any;
  evaluateFormula(column: MatrixColumn, sheet: number, row: number): void;
  evaluateFormulaRif(column: MatrixColumn, sheet: number, row: number): void;
  checkManualTouched(cur: IMatrixRif): boolean;
  setManualTouched(cur: IMatrixRif): void;
  getFormulaRif(formula: string, cur: IMatrixRif): Array<IMatrixRif>;
  resetDependencies(): void;
  evaluateDependecies(): void;
  evaluateCells(): void;
  refresh(): void;
  repaint(): void;
  setColumnVisibilityByOwnerStatus(sheet: number, owner: number, visibile: boolean): void;
  activeConfig: EidosMatrixConfig;
  activeData: EidosMatrixData;
}
export interface IEidosMatrixRequestAction {
  action: string,
  sheet: EidosMatrixSheet,
  api: IEidosMatrixApi
}
export interface IEidosMatrixRequestNewItem {
  sheet: EidosMatrixSheet,
  api: IEidosMatrixApi
}
export interface IMatrixValueChanged {
  column: MatrixColumn;
  rif: IMatrixRif;
  newValue: EidosMatrixValue;
  oldValue: EidosMatrixValue;
}
export interface IMatrixRif {
  sheet: number;
  row: number;
  column: number;
  columnName: string;
}

type MatrixColumnsGroups = Array<IMatrixColumnGroup>
export interface IMatrixColumnGroup {
  headerId?: string;
  groups: MatrixColumnsGroups;
  caption: string;
  columnStart: string | number;
  columnEnd: string | number;
  headerStyle?: object;
  alignment?: string;
  collapsible?: boolean;
  fixed?: boolean;
  fixedPosition?: 'left' | 'right';
}

export enum MatrixFormulaRangeType {
  All = '#',
  Before = '<',
  After = '>'
}
/**
 * formula is a valid js evaluable statment expression.
 * in formula you can reference a cell value using a reference sintax.
 * - reference syntax: cell reference is composed by 3 optional parts (at least one is required).
 *                     the three parts are: sheet @s, row @r and column @c
 *                     any parts can reference a relative dimension with this convetion:
 *                     - number : postion of element (first position is 1) ex. @s1@r2@c4 (esplicit all part), @r4 (current column on 4 row), @c4 (column 4 in current sheete and row)
 *                     - negative number : relative position of element start from end (first position array length) ex. @r-1 (last row)
 *                     - negative number followed by dot : relative position of element start from current position () ex. @c-1. (previuos column)
 *                     - < : all same element before current. ex. @r< (all row before current same sheet and same column)
 *                     - > : all same element after current. ex. @c> (all column after current same sheet and same row)
 *                     - # : all same element without current. ex. @r1@c# (all column of first row except current)
 *   any valid combination of this parts is a valid reference that is replaced ith is referenced value before evaluate expression
 *   for column is vali an alternative syntax that is referred to column name @@<Column name>.
 *
 * @export
 * @interface IMatrixFormula
 */
export interface IMatrixFormula {
  condition?: string;
  skipCondition?: string;
  formula: string;
}
interface IMatrixColumnLookup {
  dataSource: Array<any>,
  displayExpr: string,
  valueExpr: string,
}
/**
 * specific a column of a matrix
 *
 * @export
 * @class MatrixColumn
 */
export class MatrixColumn {
  displayFormattedValue?: (column: MatrixColumn, event: CellPreparedEvent) => string | null;
  displayCustomFormattedValue: (sheet: EidosMatrixSheet, columnName: string, value: any) => string | null;
  id: string;
  title?: string;
  name: string;
  position: number
  ref: EidosMatrixSheet;
  type: string;
  customEdit: boolean;
  cellTemplate?: string;
  lookup?: IMatrixColumnLookup;
  touch: boolean;
  formulas: Array<IMatrixFormula>;
  dependencies: Set<IMatrixRif>;
  lastException: Error | null;
  sheet: number;
  headerStyle: any;
  style: any;
  allowEditing: boolean | ((event?: EditingStartEvent, sheet?: EidosMatrixSheet) => boolean);
  isVisible: boolean | ((column: MatrixColumn, sheet: EidosMatrixSheet, data?: EidosMatrixData) => boolean);
  minWidth?: number;
  width?: number;
  editorOptions?: any;
  onChange?: (event: IMatrixValueChanged, sheet: EidosMatrixSheet, matrix: IEidosMatrixApi) => void;
  ownerBand?: number;
  tooltipText?: string;
  fixed: boolean;
  fixedPosition: 'left' | 'right';
  buttonIDs?: string[];

  constructor(ref: EidosMatrixSheet, index: number, name: string, type?: string) {
    this.id = uuid();
    this.ref = ref;
    this.sheet = ref.position;
    this.position = index;
    this.name = name;
    this.type = type ?? 'number';
    this.customEdit = false;
    this.touch = false;
    this.formulas = [];
    this.dependencies = new Set();
    this.lastException = null;
    this.allowEditing = true;
    this.isVisible = true;
    this.fixed = false;
    this.fixedPosition = 'left';
    this.displayCustomFormattedValue = (_sheet,_name,value)=>value;
  }
  hasFormula(): boolean {
    return this.formulas.filter(f => f.formula.trim() !== '').length > 0;
  }
  evaluateExceptions(e: Error) {
    this.lastException = e;
  }
}
export class EidosMatrixSheetCommandColumnCustomize {
  openDetailsIcon: string = 'fa-caret-down';
  closeDetailsIcon: string = 'fa-caret-right';
  masterDetailsText: string = '';
  masterDetailsTooltip: IEidosTooltip = {};
  detailsBackgroundColor: string = '#eee';
  detailsTextColor: string = 'black';
  detailsIcon?: string;
  detailsText: string = '';
}
export interface IEidosMaxtrixButton {
  id: string;
  text: string | ((cellinfo:any, sheet: EidosMatrixSheet) => string);
  title?: string | ((cellinfo:any, sheet: EidosMatrixSheet) => string);
  buttonClass?: string | object | ((cellinfo:any, sheet: EidosMatrixSheet) => string | object | undefined);
  buttonStylingMode?: 'primary' | 'accent' | 'warn' 
  buttonType?: 'back' | 'danger' | 'default' | 'normal' | 'success';
  visible: (cellinfo:any, sheet: EidosMatrixSheet) => boolean;
  disabled: (ellinfo:any, sheet: EidosMatrixSheet) => boolean;
  callback: (cellinfo:any, sheet: EidosMatrixSheet, matrix: IEidosMatrixApi) => void
}
/**
 * describe
 *
 * @export
 * @class MatrixSheet
 */
export class EidosMatrixSheet {
  self: EidosMatrixSheet;
  sheetCfg: IMatrixSheet;
  id: string;
  title?: string;
  name: string;
  position: number
  ref: EidosMatrixConfig;
  rowKey: string;
  columns: Array<MatrixColumn>;
  callbackButtons:IEidosMaxtrixButton[] = []
  rows: number;
  rowAlternationEnabled: boolean;
  size: CssSize;
  attributes: any;
  columnsGroups: MatrixColumnsGroups;
  gridComponent: any;
  canAddRow: boolean;
  canAddColumn: boolean;
  customActions: IEidosMatrixCustomAction[] = [];
  customBottomActions: IEidosMatrixCustomAction[] = [];
  onExecutingTask: boolean;
  getRowStyle: (data: any) => any;
  hasChild: (data: any) => boolean;
  collapsed: { [key: number]: boolean } = {};
  customizeCommandColumn: EidosMatrixSheetCommandColumnCustomize;
  isChildRow(data: any): boolean {
    if (!this.sheetCfg.getMasterKey) return false;
    return this.sheetCfg.getMasterKey(data) !== undefined;
  }
  getButtonCallback(cellInfo:any) {
    return this.sheetCfg.callbackButtons?.find(cb=>cb.id===cellInfo.column.dataField)
  }
  isVisible() {
    return this.columns.filter(c=>(typeof c.isVisible === 'function') ? c.isVisible(c,this) : c.isVisible).length>0;
  }
  initializeMasterDetailRow(): void {

  }
  getRowParentChildAttribute(data: any): Attr | undefined {
    if (!this.sheetCfg.getMasterKey) return undefined;
    const mk = this.sheetCfg.getMasterKey(data);
    if (mk === undefined) return undefined;

    const node = document.createAttribute("data-eidos-child");
    node.value = Array.isArray(mk) ? mk.join(',') : mk as string;
    return node;
  }

  constructor(ref: EidosMatrixConfig, sheetCfg: IMatrixSheet, index: number, name: string, columns: Array<IEidosMatrixColumn> | number, rows?: number) {
    this.self = this;
    this.sheetCfg = sheetCfg;
    this.id = uuid();
    this.getRowStyle = sheetCfg.getRowStyle ?? EidosMatrixSheet.emptyFunction;
    this.hasChild = sheetCfg.hasChild ?? EidosMatrixSheet.emptyFunction;
    this.onExecutingTask = false;
    this.canAddColumn = sheetCfg.canAddColumn ?? false;
    this.canAddRow = sheetCfg.canAddRow ?? false;
    this.customActions = sheetCfg.customActions ?? [];
    this.customBottomActions = sheetCfg.customBottomActions ?? [];
    this.rowKey = sheetCfg.rowKey ?? 'rowIndex'
    this.ref = ref;
    this.position = index;
    this.name = name;
    this.columns = [];
    this.rows = rows ?? 0;
    this.rowAlternationEnabled = false;
    this.size = { width: '100%', height: 'auto' } as CssSize;
    this.attributes = {};
    if (sheetCfg.size) {
      this.size.width = sheetCfg.size.width ?? this.size.width;
      this.size.height = sheetCfg.size.height ?? this.size.height;
    }
    this.rowAlternationEnabled = sheetCfg.rowAlternationEnabled ?? false;
    this.customizeCommandColumn = new EidosMatrixSheetCommandColumnCustomize();
    if (sheetCfg.customizeCommandColumn?.openDetailsIcon) this.customizeCommandColumn.openDetailsIcon = sheetCfg.customizeCommandColumn?.openDetailsIcon;
    if (sheetCfg.customizeCommandColumn?.closeDetailsIcon) this.customizeCommandColumn.closeDetailsIcon = sheetCfg.customizeCommandColumn?.closeDetailsIcon;
    if (sheetCfg.customizeCommandColumn?.masterDetailsText) this.customizeCommandColumn.masterDetailsText = sheetCfg.customizeCommandColumn?.masterDetailsText;
    if (sheetCfg.customizeCommandColumn?.detailsBackgroundColor) this.customizeCommandColumn.detailsBackgroundColor = sheetCfg.customizeCommandColumn?.detailsBackgroundColor;
    if (sheetCfg.customizeCommandColumn?.detailsTextColor) this.customizeCommandColumn.detailsTextColor = sheetCfg.customizeCommandColumn?.detailsTextColor;
    if (sheetCfg.customizeCommandColumn?.masterDetailsTooltip) this.customizeCommandColumn.masterDetailsTooltip = sheetCfg.customizeCommandColumn?.masterDetailsTooltip;
    if (sheetCfg.customizeCommandColumn?.detailsIcon) this.customizeCommandColumn.detailsIcon = sheetCfg.customizeCommandColumn?.detailsIcon;
    if (sheetCfg.customizeCommandColumn?.detailsText) this.customizeCommandColumn.detailsText = sheetCfg.customizeCommandColumn?.detailsText;

    if (sheetCfg.attributes) {
      this.attributes = sheetCfg.attributes;
    }
    if (sheetCfg.featureStyle) {
      this.attributes.class = (this.attributes.class ? this.attributes.class + ' ' : '') + sheetCfg.featureStyle.map(a => `eidos-matrix-${a}`).join(' ');
    }

    if(sheetCfg.callbackButtons) this.callbackButtons = sheetCfg.callbackButtons
    // if(sheetCfg.callbackText) this.callbackText = sheetCfg.callbackText
    // if(sheetCfg.callbackButton) this.callbackButton = sheetCfg.callbackButton
    // if(sheetCfg.callbackButtonVisibility) this.callbackButtonVisibility = sheetCfg.callbackButtonVisibility

    if (Array.isArray(columns)) {
      columns.forEach((mc, idx) => {
        const col = new MatrixColumn(this, idx, mc.name, mc.type ?? EidosMatrixSheet.getColumnDefaultType());

        let allowEditing = true;
        if(sheetCfg.readOnly){
          if((typeof sheetCfg.readOnly === 'function')) allowEditing = !sheetCfg.readOnly()
          else allowEditing = false
        }

        if (mc.formulas) {
          mc.formulas.forEach(f => {
            if (f.formula.trim() !== '') {
              col.formulas.push(f);
            }
          });
        }
        col.allowEditing = allowEditing && (mc.allowEditing ?? (col.formulas.length === 0));
        col.style = mc.style;
        col.customEdit = mc.customEdit ?? false;
        col.tooltipText = mc.tooltipText;
        col.headerStyle = mc.headerStyle;
        col.title = mc.title;
        col.isVisible = mc.isVisible ?? true;
        col.minWidth = mc.minWidth;
        col.editorOptions = mc.editorOptions;
        col.cellTemplate = mc.cellTemplate;
        col.lookup = mc.lookup;
        col.fixed = mc.fixed ?? false;
        col.fixedPosition = mc.fixedPosition ?? 'left';
        col.onChange = mc.onChange;
        col.displayFormattedValue = mc.displayFormattedValue;
        if(mc.displayCustomFormattedValue) col.displayCustomFormattedValue = mc.displayCustomFormattedValue;
        if (mc.buttonIDs) col.buttonIDs = mc.buttonIDs;
        this.columns.push(col);
      });
    } else {
      for (let idx = 0; idx < columns; idx++) {
        this.columns.push(new MatrixColumn(this, idx, EidosMatrixSheet.getColumnNameDefault(idx), EidosMatrixSheet.getColumnDefaultType()));
      }
    }

    this.columnsGroups = [];
    sheetCfg.headers.forEach(h => this.addHeaderGroup(h, this.columnsGroups));
    this.customizeColumns = this.customizeColumns.bind(this);
    if (sheetCfg.customizeCommandColumn?.detailsBackgroundColor) {
      document.documentElement.style.setProperty('--master-details-bg-color', sheetCfg.customizeCommandColumn?.detailsBackgroundColor);
    }
    if (sheetCfg.customizeCommandColumn?.detailsTextColor) {
      document.documentElement.style.setProperty('--master-details-text-color', sheetCfg.customizeCommandColumn?.detailsTextColor);
    }
  }
  static emptyFunction(): any { return {} };

  static getColumnNameDefault(idx: number) {
    let num = idx, letters = '';
    while (num >= 0) {
      letters = (num % 26 + 10).toString(36).toUpperCase() + letters
      num = Math.floor(num / 26) - 1
    }
    return letters;
  }
  static getColumnIndex(letters: string): number {
    let idx = 0, position = 1;
    const chars = letters.split('');
    while (chars.length > 0) {
      const c = chars.shift();
      idx += (c!.charCodeAt(0) - 'A'.charCodeAt(0)) * position;
      position *= 10;
    }
    return idx;
  }
  static getColumnDefaultType() {
    return 'number';
  }
  hasToolbar(): boolean {
    return this.canAddColumn || this.canAddRow || this.customActions.length > 0;
  }
  get hasBottomToolbar(): boolean {
    return this.customBottomActions.length > 0;
  }
  getColumnIndexByName(name: string): number {
    let idx = -1, s = name.toLowerCase();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].name.toLowerCase() === s) {
        idx = this.columns[i].position;
        break;
      }
    }
    return idx;
  }
  getColumnDefaultValue(c: MatrixColumn): (string | number) {
    switch (c.type) {
      case "number":
        return 0;
      case "text":
        return '';
      default:
        return '';
    }
  }
  resolveIndex(col: string | number): number {
    if (typeof col === 'string') return this.getColumnIndexByName(col);
    if (col < 0) return this.columns.length + col;
    return col - 1;
  }
  applyChanges(changes: IMatrixPropertyUpdate[]): void {
    if (!this.gridComponent) return;
    changes.forEach(c => {
      const ca = this.columns.filter(col => col.name === c.column);
      if (ca.length === 0) return;

      let v;
      switch (c.property) {
        case 'visible':
          v = ca[0].isVisible;
          break;
      }
      if (v !== undefined) this.gridComponent.instance.columnOption(c.column, c.property, v);
    })
  }
  /**
   * Initilize apparence of row (header and data)
   *
   * @param {RowClickEvent} event
   * @param {EidosMatrixSheet} sheet
   * @return {void}
   * @memberof MatrixSheet
   */
  onRowPrepared(event: RowClickEvent, sheet: EidosMatrixSheet): void {
    if (event.rowElement === undefined) return;

    switch (event.rowType) {
      case 'data':
        {
          const attrs = sheet.getRowStyle(event.data);
          if (attrs) Object.keys(attrs).forEach(k => event.rowElement.style.setProperty(k, attrs[k]));
          //set id on every row
          event.rowElement.id = `${event.key}-${event.rowIndex}`;
          const parentChildAttr = sheet.getRowParentChildAttribute(event.data);
          if (parentChildAttr !== undefined) { event.rowElement.attributes.setNamedItem(parentChildAttr); }
        }
        break;
    }

  }
  /**
   * Initilize apparence of cells (header and data)
   *
   * @param {CellClickEvent} event
   * @param {EidosMatrixSheet} sheet
   * @return {void}
   * @memberof MatrixSheet
   */
  onEditStart(event: EditingStartEvent, sheet: EidosMatrixSheet): void {
    const c = sheet.getColumnById((event.column as any).headerId);
    if (c && (typeof c.allowEditing === 'function')) {
      if (!c.allowEditing(event, sheet)) event.cancel = true;
    };
  }
  /**
   * Initilize apparence of cells (header and data)
   *
   * @param {CellClickEvent} event
   * @param {EidosMatrixSheet} sheet
   * @return {void}
   * @memberof MatrixSheet
   */
  onCellPrepared(event: CellPreparedEvent, sheet: EidosMatrixSheet): void {
    if (event.cellElement === undefined) return;

    let attrs: any = {};

    switch (event.rowType) {
      case 'header':
        {
          const h = event.column as any;
          const c = h.isBand ? sheet.getHeaderColumnById(h.headerId) : sheet.getColumnById(h.headerId);
          if (c && c.headerStyle) attrs = c.headerStyle;
          if (h.collapsible) {
            if (event.cellElement.children.length > 0) event.cellElement.children[0].setAttribute('style', 'width: 100%;');
          }
        }
        break;
      case 'data':
        {
          // Cells
          const h = event.column as any;
          if (h) {
            if (h.style) attrs = h.style;

            const colConfig = sheet.getColumnById(h.headerId);

            if (colConfig) {
              if (colConfig.displayFormattedValue) {
                const v = colConfig.displayFormattedValue(h, event);
                if (v != null) event.cellElement.innerText = v;
              }

              if (!(colConfig.tooltipText === undefined)) {
                event.cellElement.title = colConfig.tooltipText;
              }
            }
          }
        }
        break;
    }

    Object.keys(attrs).forEach(k => event.cellElement.style.setProperty(k, attrs[k]));
  }
  /**
   * Initialize columns definition
   * - prepare header groups
   * - hide row index column
   * - set columns visibility
   *
   * @param {Array<DevExpress.ui.dxDataGrid.Column>} columns
   * @memberof MatrixSheet
   */
  customizeColumns(columns: Array<DevExpress.ui.dxDataGrid.Column>) {
    if (this.sheetCfg.getMasterKey) {
      const cmdCol = new MatrixColumn(this, 0, '__ROWCOMMAND__', 'command');
      cmdCol.title = '';
      //this.columns.unshift(cmdCol);
      if (columns.length > 0) {
        if (columns[0].name !== '__ROWCOMMAND__') {
          columns.unshift({
            cellTemplate: 'matrixCommandTemplate',
            allowFixing: true,
            fixed: true,
            fixedPosition: 'left',
            width: this.sheetCfg.customizeCommandColumn?.size ?? 20,
            minWidth: this.sheetCfg.customizeCommandColumn?.size ?? 20,
            allowEditing: false,
            caption: '',
            name: '__ROWCOMMAND__',
          });
        } else {
          columns[0].cellTemplate = 'matrixCommandTemplate';
          columns[0].allowFixing = true;
          columns[0].fixed = true;
          columns[0].fixedPosition = 'left';
          columns[0].width = this.sheetCfg.customizeCommandColumn?.size ?? 20;
          columns[0].minWidth = this.sheetCfg.customizeCommandColumn?.size ?? 20;
          columns[0].allowEditing = false;
          columns[0].caption = '';
        }
      }
    }
    //Add header grouping if needed (Band)
    this.columnsGroups.forEach(g => {
      this.addHeaderBand(columns, g);
    });

    //customize cells
    columns.forEach(c => {
      if (c.isBand) {
        const colany = c as any;
        if (colany.collapsible) {
          c.headerCellTemplate = 'matrixCollasableGroupTemplate';
        }
        return; //header band
      }

      const col = this.columns.find(col => c.dataField === col.name);
      if (!col) return;

      //copy owner band from dxColum to report info in eidos config
      col.ownerBand = c.ownerBand;

      if (col.minWidth !== undefined) c.minWidth = col.minWidth;
      if (c.minWidth === undefined) c.minWidth = this.getColumnDefaultMinWidth();

      if (col.width !== undefined) c.width = col.width;
      else c.width = c.minWidth;

      c.visible = (typeof col.isVisible === 'function') ? col.isVisible(col, this) : col.isVisible;

      c.allowEditing = (typeof col.allowEditing === 'function') ? col.allowEditing() : col.allowEditing;

      if (col.fixed) {
        c.allowFixing = true;
        c.fixed = col.fixed;
        c.fixedPosition = col.fixedPosition ?? 'left';
      }

      switch (col.type) {
        case 'date':
          c.dataType = 'date';
          break;
        case 'datetime':
          c.dataType = 'datetime';
          break;
        case 'boolean':
          c.dataType = 'boolean';
          break;
        case 'switch':
          c.dataType = 'boolean';
          c.cellTemplate = 'matrixBooleanTemplate';
          c.editCellTemplate = 'matrixBooleanTemplateEdit';
          break;
        case 'number':
          c.dataType = 'number';
          break;
        case 'command':
          c.cellTemplate = 'matrixCommandTemplate';
          break;
        case 'button':
          c.cellTemplate = 'matrixButtonTemplate';
          break;
        case 'action-menu':
          c.cellTemplate = 'matrixActionMenuTemplate';
          break;

      }

      if (col.lookup) {
        c.lookup = col.lookup;
      }

      if (col.customEdit) {
        switch (col.type) {
          case 'number':
            c.cellTemplate = 'matrixNumberTemplate';
            c.editCellTemplate = 'matrixNumberTemplateEdit';
            break;
        }
      }
      c.editorOptions = col.editorOptions;
      c.caption = col.title ?? col.name;

      //c.caption += ' [' + col.position;
      //add property not in model
      const colany = c as any;
      colany.headerId = col.id;
    });
  }

  getColumnDefaultMinWidth(): number {
    return 80;
  }
  /**
   * search a group of header by id and return it if found
   * return undefined if group with this id not exist
   *
   * @param {string} id
   * @return {*}  {(IMatrixColumnGroup|undefined)}
   * @memberof MatrixSheet
   */
  getHeaderColumnById(id: string): IMatrixColumnGroup | undefined {
    /**
     * local function for recorsive search
     *
     * @param {MatrixColumnsGroups} groups
     * @param {string} id
     * @return {*}  {(IMatrixColumnGroup|undefined)}
     */
    function getColumnGroup(groups: MatrixColumnsGroups, id: string): IMatrixColumnGroup | undefined {
      const c = groups.filter(g => g.headerId === id);
      if (c.length > 0) return c[0];
      const childs = groups.filter(g => g.groups.length > 0).map(g => g.groups).flat(1);
      if (childs.length > 0) return getColumnGroup(childs, id);
      return undefined
    }

    return getColumnGroup(this.columnsGroups, id);
  }
  /**
   * search a group of header by id and return it if found
   * return undefined if group with this id not exist
   *
   * @param {string} id
   * @return {*}  {(IMatrixColumnGroup|undefined)}
   * @memberof MatrixSheet
   */
  getColumnById(id: string): MatrixColumn | undefined {
    const c = this.columns.filter(c => c.id === id);
    if (c.length > 0) return c[0];
    return undefined;
  }
  addHeaderBand(columns: Array<DevExpress.ui.dxDataGrid.Column>, group: IMatrixColumnGroup, parent?: number) {
    const band = {
      caption: group.caption,
      isBand: true,
      ownerBand: parent,
      headerId: group.headerId ?? uuid(),
      collapsible: group.collapsible,
      allowFixing: true,
      fixed: group.fixed ?? false,
      fixedPosition: group.fixedPosition ?? 'left'
    };
    const position = columns.push(band) - 1;
    group.groups.forEach(g => this.addHeaderBand(columns, g, position));

    if (group.groups.length === 0) {
      const minStart = this.resolveIndex(group.columnStart);
      const maxEnd = this.resolveIndex(group.columnEnd);
      if (minStart > -1 && maxEnd > -1) {
        columns.filter((_, idx) => idx >= this.resolveIndex(group.columnStart) && idx <= this.resolveIndex(group.columnEnd))
          .forEach(c => {
            c.ownerBand = position;
          });
      }
    }

  }
  /**
   * aggiunge o sostituisce una formula in una colonna
   * Dopo l'aggiuta sostituzione vengono ricalcolate le dipendenze
   *
   * @param {(string|number)} column
   * @param {string} formula
   * @return {*}
   * @memberof MatrixSheet
   */
  setFormula(column: string | number, formula: string, condition?: string) {
    let idx = this.resolveIndex(column);
    if (idx < 0) return;

    this.columns[idx].formulas.push({ formula: formula, condition: condition } as IMatrixFormula);
    this.ref.formulaReferenceValidation(this.columns[idx]);
  }
  initializeCellValue(data: IEidosMatrixValue, columnName: string, value: EidosMatrixValue) {
    //add usefult property
    data[`__${columnName}`] = {
      isTouched: false,
      isManualTouched: false
    } as IMatrixCellStatus;
    //set value
    data[columnName] = value;
  }
  initializeDataRow(row: IEidosMatrixValue, rowIdx: number): IEidosMatrixValue {
    let data = { __rowIndex: rowIdx, __sheetIndex: this.position } as IEidosMatrixValue;

    if (!row) {
      //all default
      data[this.rowKey] = rowIdx;
      this.columns.filter(c => c.name !== this.rowKey).forEach(c => this.initializeCellValue(data, c.name, this.getColumnDefaultValue(c)));
      return data;
    }

    //add key if missing
    data[this.rowKey] = row[this.rowKey] ? row[this.rowKey] : rowIdx;
    this.columns.filter(c => c.name !== this.rowKey).forEach(c => {
      //add columns value
      this.initializeCellValue(data, c.name, row[c.name] ? row[c.name] : this.getColumnDefaultValue(c));
    });

    return data;
  }
  initializeData(rows: Array<IEidosMatrixValue>): Array<IEidosMatrixValue> {
    const r = rows ?? [];
    const data: Array<IEidosMatrixValue> = [];
    for (let rowIdx = 0; rowIdx < this.rows; rowIdx++) {
      data[rowIdx] = this.initializeDataRow(r[rowIdx], rowIdx);
    }
    return data;
  }

  addHeaderGroup(header: IMatrixColumnGroup, current: MatrixColumnsGroups): void {
    const group = {
      headerId: header.headerId ?? uuid(),
      caption: header.caption,
      columnStart: header.columnStart,
      columnEnd: header.columnEnd,
      headerStyle: header.headerStyle,
      groups: [],
      collapsible: header.collapsible,
      allowFixing: true,
      fixed: header.fixed ?? false,
      fixedPosition: header.fixedPosition ?? 'left',
    };
    header.groups.forEach(h => {
      //controlli di congruenza
      const minStart = this.resolveIndex(header.columnStart);
      const maxEnd = this.resolveIndex(header.columnEnd);
      if (this.resolveIndex(h.columnStart) < minStart) h.columnStart = minStart + 1;
      if (this.resolveIndex(h.columnEnd) > maxEnd) h.columnEnd = maxEnd + 1;

      this.addHeaderGroup(h, group.groups);
    });
    current.push(group);
  }
}
export interface IEidosMatrixColumn {
  allowEditing?: boolean | ((event?: EditingStartEvent, sheet?: EidosMatrixSheet) => boolean)
  position?: number,
  title?: string;
  name: string,
  type: string,
  customEdit?: boolean,
  formulas?: Array<IMatrixFormula>;
  style?: any;
  headerStyle?: any;
  isVisible?: boolean | ((column: MatrixColumn, sheet: EidosMatrixSheet, data?: EidosMatrixData) => boolean);
  minWidth?: number;
  width?: number;
  editorOptions?: any;
  displayFormattedValue?: (column: MatrixColumn, event: CellPreparedEvent) => string | null;
  displayCustomFormattedValue?: (sheet: EidosMatrixSheet, columnName: string, value: any) => string | null;
  onChange?: (event: IMatrixValueChanged, sheet: EidosMatrixSheet, matrix: IEidosMatrixApi) => void;
  cellTemplate?: string;
  lookup?: IMatrixColumnLookup;
  tooltipText?: string;
  fixed?: boolean;
  fixedPosition?: 'left' | 'right';
  buttonID?: string;
  /** IDs of `callbackButtons` shown inside an `action-menu` column. */
  buttonIDs?: string[];
  // buttonText?: string;
  // buttonColor?: string;
}
interface IMatrixSheet {
  getRowStyle?: (data: any) => any;
  getMasterKey?: (data: any) => undefined | string | string[];
  hasChild?: (data: any) => boolean;
  readOnly?: boolean | (() => boolean);
  rowKey: string;
  size?: CssSize;
  name?: string,
  type?: string,
  headers: MatrixColumnsGroups;
  columns: Array<IEidosMatrixColumn> | number;
  callbackButtons?:IEidosMaxtrixButton[]
  // callbackText?:string;
  // callbackButtonVisibility?: (cellinfo:any, sheet: EidosMatrixSheet) => boolean
  // callbackButton?: (cellinfo:any, sheet: EidosMatrixSheet, matrix: IEidosMatrixApi) => void;
  rows?: number
  attributes?: any;
  featureStyle?: Array<string>;
  rowAlternationEnabled?: boolean;
  canAddRow?: boolean;
  canAddColumn?: boolean;
  customActions?: IEidosMatrixCustomAction[];
  customBottomActions?: IEidosMatrixCustomAction[];
  customizeCommandColumn?: IEidosMatrixCommandColumCustomize
}
interface IEidosMatrixCommandColumCustomize {
  openDetailsIcon?: string,
  closeDetailsIcon?: string,
  masterDetailsText?: string,
  masterDetailsTooltip?: IEidosTooltip,
  detailsBackgroundColor?: string,
  detailsTextColor?: string,
  size?: number;
  detailsIcon?: string,
  detailsText?: string,
}
interface IEidosMatrixCustomAction {
  id: string;
  text?: string;
  buttonStylingMode?: 'text' | 'outlined' | 'contained';
  buttonType?: 'back' | 'danger' | 'default' | 'normal' | 'success';
  visible: () => boolean;
  disabled: () => boolean;
}

export class MatrixCallback {
  onAfterInitialize: (api: IEidosMatrixApi) => void;
  onAfterDataInitialize: (api: IEidosMatrixApi) => void;
  onAfterDataChange?: (colValueChanged: IMatrixValueChanged) => void;
  constructor() {
    this.onAfterInitialize = () => { };
    this.onAfterDataInitialize = () => { };
    this.onAfterDataChange = () => { };
  }
}
export interface IMatrixConfig {
  size?: CssSize;
  sheets: Array<IMatrixSheet>;
  callback?: MatrixCallback;
  formulaParameters?:BehaviorSubject<IEidosMatrixValue>;
  formulaFunctions?:IEidosMatrixGenericFun;
  onChangeParametersFun?:IEidosMatrixValueFun;
  triggerAction?: (action: string, callback:(resquestAction:IEidosMatrixRequestAction)=>void, sheetIndex?: number) => void;
};

interface CssSize {
  width: string;
  height: string;
};
export class EidosMatrixConfig {
  size: CssSize;
  sheets: Array<EidosMatrixSheet>;
  callback: MatrixCallback;
  api: IEidosMatrixApi | undefined;
  constructor(cfg: IMatrixConfig) {
    this.sheets = [];
    this.size = { width: cfg.size?.width ?? '100%', height: cfg.size?.height ?? '100%' };
    cfg.sheets.forEach((s, idx) => {
      const ms = new EidosMatrixSheet(this, s, idx, s.name ?? 'sheet ' + (idx + 1), s.columns, s.rows);
      ms.size = cfg.sheets[idx] ? cfg.sheets[idx].size ?? ms.size : ms.size;
      ms.attributes = cfg.sheets[idx] ? cfg.sheets[idx].attributes ?? ms.attributes : ms.attributes;
      this.sheets.push(ms);
    });

    this.callback = cfg.callback ? cfg.callback : new MatrixCallback();
  }
  static EmptyConfig: IMatrixConfig = {
    sheets: []
  }
  static TestConfig: IMatrixConfig = {
    size: { width: '100%', height: 'auto' },
    sheets: [
      {
        rowKey: 'rowIndex',
        size: { width: '50%', height: 'auto' },
        name: 'first sheet',
        headers: [
          {
            groups: [
              {
                groups: [],
                caption: 'first.A',
                columnStart: 2,
                columnEnd: 3
              }, {
                groups: [],
                caption: 'first.B',
                columnStart: 4,
                columnEnd: 5
              }
            ],
            caption: 'first',
            columnStart: 2,
            columnEnd: 5
          }, {
            groups: [],
            caption: 'second',
            columnStart: 6,
            columnEnd: 7,
            headerStyle: {
              "background-color": '#008',
              color: 'white',
              align: 'center'
            }
          }
        ],
        featureStyle: ['nopad'],
        attributes: { style: { padding: '5px;' } },
        columns: [
          {
            name: 'field1',
            type: 'number'
          }, {
            name: 'field2',
            type: 'number'
          }, {
            name: 'field3',
            type: 'number'
          }, {
            name: 'field4',
            type: 'number',
            formulas: [{ formula: '@c1 + @c2 + @c3' }]
          }, {
            name: 'field5',
            type: 'number',
            formulas: [{ formula: '@c<' }],
            style: { 'font-weight': 'bold' },
            headerStyle: {
              "background-color": '#800',
              color: 'white',
              align: 'center'
            }
          }, {
            name: 'field6',
            type: 'number',
            formulas: [{ formula: '@c-1. * 2' }],
            style: { 'font-weight': 'bold' },
            headerStyle: {
              "background-color": '#008',
              color: 'white',
              align: 'center'
            }
          }
        ],
        rows: 100
      }, {
        size: { width: '100%', height: 'auto' },
        rowKey: 'rowIndex',
        headers: [],
        columns: 100,
        rows: 10
      }]
  }
  formulaReferenceValidation(_: MatrixColumn) {
    //TODO
  }
  initialize(matrixData: EidosMatrixData): EidosMatrixData {
    const data: EidosMatrixData = [];

    this.sheets.forEach((s, sheetIdx) => {
      data[sheetIdx] = s.initializeData(matrixData[sheetIdx]);
    });

    return data;
  }
}

export interface IMatrixPropertyUpdate {
  sheet: number;
  column: string;
  property: string;
}
